import { useEffect, useMemo, useState } from 'react';
import { getSession } from '~/lib/auth';

/**
 * Bookings — every reservation that came through the public booking form.
 * Admin can update status (pending → confirmed → completed), record a
 * sheet # from the paper chart, and add a quick note. Other fields
 * (deposit_status, slot_start, email) are managed by other systems
 * (Stripe webhook, intake form) and are intentionally read-only here.
 *
 * Backed by `/api/admin/bookings` (admin-only, RLS-enforced).
 */

type Status = 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
type DepositStatus = 'not_required' | 'pending' | 'paid' | 'refunded' | 'failed';

interface Booking {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  slot_start: string;
  slot_end: string;
  status: Status;
  deposit_status: DepositStatus | null;
  deposit_amount_cents: number | null;
  consult_type: 'phone' | 'free_video' | 'paid_in_person';
  sheet_number: string | null;
  notes: string | null;
  optional_concern: string | null;
  created_at: string;
}

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.valleyvitalitywellness.com';

const STATUS_OPTIONS: Status[] = ['pending', 'confirmed', 'completed', 'cancelled', 'no_show'];
const STATUS_LABEL: Record<Status, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No show',
};
const STATUS_COLOR: Record<Status, { bg: string; fg: string }> = {
  pending:   { bg: 'rgba(244, 201, 93, 0.18)',  fg: '#b87a1f' },
  confirmed: { bg: 'rgba(95, 168, 162, 0.18)',  fg: 'var(--primary)' },
  completed: { bg: 'rgba(127, 201, 122, 0.18)', fg: '#3f7a3a' },
  cancelled: { bg: 'rgba(170, 170, 170, 0.18)', fg: 'var(--text-muted)' },
  no_show:   { bg: 'rgba(196, 69, 69, 0.18)',   fg: '#9a3535' },
};

const DEPOSIT_LABEL: Record<DepositStatus, string> = {
  not_required: '—',
  pending: 'Awaiting',
  paid: 'Paid',
  refunded: 'Refunded',
  failed: 'Failed',
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export function BookingsPage() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Status | 'all'>('all');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Partial<Pick<Booking, 'status' | 'sheet_number' | 'notes'>>>>({});

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const session = getSession();
      if (!session) throw new Error('Not signed in');
      const res = await fetch(`${API_BASE}/api/admin/bookings`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = (await res.json()) as { bookings: Booking[] };
      setBookings(data.bookings ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function save(b: Booking) {
    const draft = drafts[b.id];
    if (!draft || Object.keys(draft).length === 0) return;
    setSavingId(b.id);
    setError(null);
    try {
      const session = getSession();
      if (!session) throw new Error('Not signed in');
      const res = await fetch(`${API_BASE}/api/admin/bookings/${b.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(draft),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `Save failed (${res.status})`);
      }
      const data = (await res.json()) as { booking: Booking };
      setBookings((prev) => prev.map((x) => (x.id === b.id ? data.booking : x)));
      setDrafts((prev) => {
        const { [b.id]: _, ...rest } = prev;
        return rest;
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingId(null);
    }
  }

  const visible = useMemo(() => {
    if (filter === 'all') return bookings;
    return bookings.filter((b) => b.status === filter);
  }, [bookings, filter]);

  const countByStatus = useMemo(() => {
    const out: Record<Status | 'all', number> = {
      all: bookings.length,
      pending: 0, confirmed: 0, completed: 0, cancelled: 0, no_show: 0,
    };
    for (const b of bookings) out[b.status]++;
    return out;
  }, [bookings]);

  const updateDraft = (id: string, patch: Partial<Booking>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] ?? {}), ...patch } }));
  };
  const isDirty = (id: string) => {
    const d = drafts[id];
    return d && Object.keys(d).length > 0;
  };

  return (
    <div style={{ maxWidth: 1100, display: 'grid', gap: 20 }}>
      <header>
        <h1 style={{ fontSize: 32, color: 'var(--primary)', margin: 0, fontFamily: 'var(--font-display)' }}>Bookings</h1>
        <p style={{ color: 'var(--text-muted)', margin: '4px 0 0', maxWidth: 720 }}>
          Every reservation from the public booking form. Update status, log the paper-chart sheet #, and add notes.
          Address + service detail live in Google Calendar — see the Calendar tab for the full visit view.
        </p>
      </header>

      {error && (
        <div style={{ padding: 12, background: 'rgba(196, 69, 69, 0.08)', border: '1px solid rgba(196, 69, 69, 0.3)', borderRadius: 8, color: '#9a3535', fontSize: 13 }}>
          {error}
        </div>
      )}

      <nav role="tablist" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--line-soft)', overflowX: 'auto' }}>
        {(['all', ...STATUS_OPTIONS] as const).map((s) => {
          const active = filter === s;
          const label = s === 'all' ? 'All' : STATUS_LABEL[s];
          return (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setFilter(s)}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '10px 14px',
                fontSize: 14,
                fontWeight: active ? 600 : 500,
                color: active ? 'var(--primary)' : 'var(--text-muted)',
                borderBottom: active ? '2px solid var(--teal)' : '2px solid transparent',
                marginBottom: -1,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              {label}
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{countByStatus[s]}</span>
            </button>
          );
        })}
      </nav>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : visible.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>No bookings in this view.</p>
      ) : (
        <div style={{ background: 'var(--card)', border: '1px solid var(--line-soft)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: 'var(--bg)', textAlign: 'left' }}>
                <th style={th}>Slot</th>
                <th style={th}>Name</th>
                <th style={th}>Type</th>
                <th style={th}>Status</th>
                <th style={th}>Deposit</th>
                <th style={th}>Sheet #</th>
                <th style={th}>Notes</th>
                <th style={{ ...th, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((b) => {
                const status = (drafts[b.id]?.status ?? b.status) as Status;
                const sheet = drafts[b.id]?.sheet_number ?? b.sheet_number ?? '';
                const notes = drafts[b.id]?.notes ?? b.notes ?? '';
                const dirty = isDirty(b.id);
                return (
                  <tr key={b.id} style={{ borderTop: '1px solid var(--line-soft)' }}>
                    <td style={td}>
                      <div>{fmtDate(b.slot_start)}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                        {Math.round((new Date(b.slot_end).getTime() - new Date(b.slot_start).getTime()) / 60_000)} min
                      </div>
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: 'var(--primary)' }}>{b.name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{b.email}</div>
                      {b.phone && <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{b.phone}</div>}
                    </td>
                    <td style={{ ...td, fontSize: 11.5 }}>
                      {b.consult_type === 'phone' ? 'Phone consult' : b.consult_type === 'free_video' ? 'Video consult' : 'In-person'}
                    </td>
                    <td style={td}>
                      <select
                        value={status}
                        onChange={(e) => updateDraft(b.id, { status: e.target.value as Status })}
                        style={{
                          ...inputStyle,
                          background: STATUS_COLOR[status].bg,
                          color: STATUS_COLOR[status].fg,
                          fontWeight: 600,
                          fontSize: 12,
                          padding: '4px 8px',
                          border: 'none',
                          borderRadius: 999,
                        }}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s} style={{ color: 'var(--text)', background: 'var(--card)' }}>
                            {STATUS_LABEL[s]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ ...td, fontSize: 12 }}>
                      <div>{b.deposit_status ? DEPOSIT_LABEL[b.deposit_status] : '—'}</div>
                      {b.deposit_amount_cents != null && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          ${(b.deposit_amount_cents / 100).toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td style={td}>
                      <input
                        type="text"
                        value={sheet}
                        onChange={(e) => updateDraft(b.id, { sheet_number: e.target.value || null })}
                        placeholder="#"
                        style={{ ...inputStyle, width: 80, fontFamily: "'SF Mono', Menlo, Consolas, monospace" }}
                      />
                    </td>
                    <td style={td}>
                      <input
                        type="text"
                        value={notes}
                        onChange={(e) => updateDraft(b.id, { notes: e.target.value || null })}
                        placeholder="Optional"
                        style={{ ...inputStyle, width: '100%', minWidth: 180 }}
                      />
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {dirty && (
                        <button
                          type="button"
                          onClick={() => save(b)}
                          disabled={savingId === b.id}
                          style={{
                            background: 'var(--primary)', color: 'var(--card)',
                            border: 'none', padding: '5px 12px', borderRadius: 6,
                            fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          {savingId === b.id ? 'Saving…' : 'Save'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

const td: React.CSSProperties = {
  padding: '12px',
  verticalAlign: 'top',
};

const inputStyle: React.CSSProperties = {
  fontFamily: 'inherit',
  fontSize: 13,
  padding: '6px 8px',
  border: '1px solid var(--line)',
  borderRadius: 6,
  background: 'var(--card)',
  color: 'var(--text)',
  outline: 'none',
};
